import { useCallback, useEffect, useRef, useState } from 'react';

import { queryMicPermission } from './micPermission';

/**
 * Раннее «микрофон запрещён» — до того, как человек вошёл в разговор.
 *
 * <p>Зачем отдельный хук, а не проверка внутри {@link useVoiceRoom}: тот хук живёт только вместе
 * с комнатой, то есть узнать о запрете через него можно лишь ПОСЛЕ входа, когда остальные уже
 * ждут и слушают тишину. Замер прода 26.08: 32 сеанса из 301 приезжают с `mic_denied`, и это самая
 * частая техническая беда — половина звонков из Android-браузера.
 *
 * <p>Верим только ответу `denied`: Safari этого API не имеет вовсе, а Android ≤12 отвечает
 * «granted» и на отобранное разрешение (то же уже проходили на пушах). Поэтому хук умеет говорить
 * только «точно запрещено», предупреждение на этом и строится, а вход в разговор не блокируется
 * никогда — источником истины остаётся фактическая попытка захвата.
 *
 * @param enabled спрашивать ли вообще: незачем дёргать разрешения на карточке, куда человек и
 *     войти не может
 * @returns `denied` — точно запрещено; `recheck` — спросить заново, не дожидаясь возврата на
 *     вкладку (нужно там, где человек чинит разрешение, не уходя со страницы)
 */
export const useMicPermissionDenied = (
  enabled: boolean
): { denied: boolean; recheck: () => void } => {
  const [denied, setDenied] = useState(false);
  // Чтобы `recheck` не пересоздавался на каждое изменение `enabled` и не тащил за собой
  // перезапуск эффектов у тех, кто положит его в зависимости.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const [checkToken, setCheckToken] = useState(0);
  const recheck = useCallback(() => {
    if (enabledRef.current) {
      setCheckToken((token) => token + 1);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setDenied(false);
      return undefined;
    }

    let cancelled = false;
    const check = async () => {
      const state = await queryMicPermission();
      if (!cancelled) {
        setDenied(state === 'denied');
      }
    };
    void check();

    // Человек уходит чинить разрешение в настройки — в приложении буквально в другое приложение —
    // и возвращается. Без перепроверки на возврате предупреждение висело бы и после починки:
    // оно врало бы ровно тому, кто послушался совета, и обесценило бы себя со второго раза.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, checkToken]);

  return { denied, recheck };
};
